/**
 * What is drawn over the buildings: the street network, the supply-temperature
 * reading, and València's districts.
 *
 * A dropdown rather than three loose buttons in the bar, which is what the
 * legacy toolbar had. They belong together — each one answers "what am I looking
 * at" rather than "which buildings" — and the trigger can then say how many
 * overlays are on, which three separate buttons could only say by all being
 * visible at once.
 *
 * The district choropleth used to live here as a third checkbox. It moved to
 * ViewSwitch: Plano, Barrios and Distritos are mutually exclusive readings of
 * the stock, and a checkbox says the opposite — that it combines with whatever
 * else is ticked.
 */

import { Dropdown } from "./Dropdown";
import type { BuildingPaint } from "./useMapLibre";

export interface Layers {
  /** The street network with its 4G/5G viability colouring. */
  tramos: boolean;
  /** Which reading the buildings are painted by. */
  paint: BuildingPaint;
}

interface Props {
  layers: Layers;
  onChange: (next: Layers) => void;
  disabled?: boolean;
}

function Toggle({
  checked, onChange, children, hint,
}: {
  checked: boolean;
  onChange: (on: boolean) => void;
  children: string;
  hint: string;
}) {
  return (
    <label className="layer-toggle">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        {children}
        <span className="layer-hint">{hint}</span>
      </span>
    </label>
  );
}

export function LayerMenu({ layers, onChange, disabled }: Props) {
  const on = [layers.tramos, layers.paint === "temperatura"].filter(Boolean).length;

  return (
    <Dropdown
      id="layers"
      label="Capas"
      value={on === 0 ? "Ninguna" : `${on}`}
      active={on > 0}
      disabled={disabled}
      onReset={() => onChange({ tramos: false, paint: "letra" })}
    >
      <div className="layer-list">
        <Toggle
          checked={layers.tramos}
          onChange={(v) => onChange({ ...layers, tramos: v })}
          hint="Tramos de calle por viabilidad 4G/5G"
        >
          Red de distrito
        </Toggle>

        <Toggle
          checked={layers.paint === "temperatura"}
          onChange={(v) => onChange({ ...layers, paint: v ? "temperatura" : "letra" })}
          hint="Colorea los edificios por la temperatura de impulsión que necesitan, en vez de por su letra"
        >
          T impulsión
        </Toggle>
      </div>
    </Dropdown>
  );
}
